// SolarInstallationContact.js - Solar installation themed contact section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a solar installation themed Contact section with a form and contact information
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the contact section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The contact section elements
 */
export const createSolarInstallationContactElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const contactSectionClass = 'solar-contact-section';
  const contactContainerClass = 'solar-contact-container';
  const contactHeaderClass = 'solar-contact-header';
  const contactTitleClass = 'solar-contact-title';
  const contactSubtitleClass = 'solar-contact-subtitle';
  const contactContentClass = 'solar-contact-content';
  const contactInfoClass = 'solar-contact-info';
  const contactInfoItemClass = 'solar-contact-info-item';
  const contactInfoIconClass = 'solar-contact-info-icon';
  const contactInfoTextClass = 'solar-contact-info-text';
  const contactFormWrapperClass = 'solar-contact-form-wrapper';
  const contactFormClass = 'solar-contact-form';
  const contactFormGroupClass = 'solar-contact-form-group';
  const contactFormLabelClass = 'solar-contact-form-label';
  const contactFormInputClass = 'solar-contact-form-input';
  const contactFormTextareaClass = 'solar-contact-form-textarea';
  const contactFormButtonClass = 'solar-contact-form-button';
  const contactFormSuccessClass = 'solar-contact-form-success';
  const contactFormErrorClass = 'solar-contact-form-error';
  
  // Desktop Styles - Solar themed with whitish background
  const contactSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 80%, rgba(74, 222, 128, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.03) 0%, transparent 50%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)
    `,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const contactContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const contactHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const contactTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    color: '#0f2341',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const contactSubtitleStyles = {
    fontSize: '20px',
    color: '#475569',
    lineHeight: '1.6',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const contactContentStyles = {
    display: 'flex',
    width: '100%',
    gap: '50px',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  };

  const contactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    flex: '1',
    maxWidth: '400px'
  };

  const contactInfoItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    padding: '25px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  };

  const contactInfoIconStyles = {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    backgroundColor: '#4ade80',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: '20px',
    padding: '8px',
    flexShrink: '0',
    boxShadow: '0 4px 10px rgba(74, 222, 128, 0.3)'
  };

  const contactInfoTextStyles = {
    fontSize: '16px',
    color: '#475569',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif'
  };

  const contactFormWrapperStyles = {
    flex: '1',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid rgba(74, 222, 128, 0.2)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '40px',
    position: 'relative',
    overflow: 'hidden'
  };

  const contactFormStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    width: '100%',
    position: 'relative',
    zIndex: '2'
  };

  const contactFormGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const contactFormLabelStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0f2341',
    fontFamily: '"Inter", sans-serif'
  };

  const contactFormInputStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '16px',
    color: '#0f2341',
    backgroundColor: '#f8fafc',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const contactFormInputFocusStyles = {
    borderColor: '#4ade80',
    backgroundColor: '#ffffff',
    boxShadow: '0 0 0 3px rgba(74, 222, 128, 0.1)'
  };

  const contactFormTextareaStyles = {
    padding: '14px 16px',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '16px',
    color: '#0f2341',
    backgroundColor: '#f8fafc',
    fontFamily: '"Inter", sans-serif',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const contactFormButtonStyles = {
    padding: '16px 32px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#4ade80',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)',
    position: 'relative',
    overflow: 'hidden'
  };

  const contactFormButtonHoverStyles = {
    backgroundColor: '#22c55e',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(74, 222, 128, 0.4)'
  };

  const contactFormSuccessStyles = {
    padding: '16px 20px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #4ade80',
    fontFamily: '"Inter", sans-serif'
  };

  const contactFormErrorStyles = {
    padding: '16px 20px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginTop: '20px',
    display: 'none',
    border: '1px solid #ef4444',
    fontFamily: '"Inter", sans-serif'
  };
  
  // Tablet Styles
  const tabletContactContentStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px'
  };
  
  const tabletContactInfoStyles = {
    maxWidth: '100%',
    width: '100%'
  };
  
  const tabletContactFormWrapperStyles = {
    maxWidth: '100%',
    width: '100%'
  };
  
  // Mobile Styles
  const mobileContactHeaderStyles = {
    marginBottom: '30px'
  };
  
  const mobileContactTitleStyles = {
    fontSize: '36px'
  };
  
  const mobileContactSubtitleStyles = {
    fontSize: '18px'
  };
  
  const mobileContactFormWrapperStyles = {
    padding: '30px 20px'
  };

  const mobileContactInfoItemStyles = {
    padding: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(contactSectionClass, contactSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContainerClass, contactContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(contactHeaderClass, contactHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(contactTitleClass, contactTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactSubtitleClass, contactSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contactContentClass, contactContentStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoClass, contactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoItemClass, contactInfoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoIconClass, contactInfoIconStyles);
    newRegistry[DEVICES.DESKTOP].set(contactInfoTextClass, contactInfoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormWrapperClass, contactFormWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormClass, contactFormStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormGroupClass, contactFormGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormLabelClass, contactFormLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormInputClass, contactFormInputStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormTextareaClass, contactFormTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormButtonClass, contactFormButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormSuccessClass, contactFormSuccessStyles);
    newRegistry[DEVICES.DESKTOP].set(contactFormErrorClass, contactFormErrorStyles);
    
    // Hover and focus styles
    newRegistry[DEVICES.DESKTOP].set(`${contactFormInputClass}:focus`, contactFormInputFocusStyles);
    newRegistry[DEVICES.DESKTOP].set(`${contactFormTextareaClass}:focus`, contactFormInputFocusStyles);
    newRegistry[DEVICES.DESKTOP].set(`${contactFormButtonClass}:hover`, contactFormButtonHoverStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contactContentClass, tabletContactContentStyles);
    newRegistry[DEVICES.TABLET].set(contactInfoClass, tabletContactInfoStyles);
    newRegistry[DEVICES.TABLET].set(contactFormWrapperClass, tabletContactFormWrapperStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(contactHeaderClass, mobileContactHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(contactTitleClass, mobileContactTitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactSubtitleClass, mobileContactSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(contactFormWrapperClass, mobileContactFormWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(contactInfoItemClass, mobileContactInfoItemStyles);
    
    return newRegistry;
  });
  
  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    contactSectionClass,
    contactContainerClass,
    contactHeaderClass,
    contactTitleClass,
    contactSubtitleClass,
    contactContentClass,
    contactInfoClass,
    contactInfoItemClass,
    contactInfoIconClass,
    contactInfoTextClass,
    contactFormWrapperClass,
    contactFormClass,
    contactFormGroupClass,
    contactFormLabelClass,
    contactFormInputClass,
    contactFormTextareaClass,
    contactFormButtonClass,
    contactFormSuccessClass,
    contactFormErrorClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Content container
  const contentId = Date.now() + 5;
  
  // Contact information elements
  const infoContainerId = Date.now() + 6;
  
  // Address info
  const addressItemId = Date.now() + 7;
  const addressIconId = Date.now() + 8;
  const addressTextId = Date.now() + 9;
  const addressTextLine2Id = Date.now() + 100;
  
  // Phone info
  const phoneItemId = Date.now() + 10;
  const phoneIconId = Date.now() + 11;
  const phoneTextId = Date.now() + 12;
  const phoneTextLine2Id = Date.now() + 101;
  
  // Email info
  const emailItemId = Date.now() + 13;
  const emailIconId = Date.now() + 14;
  const emailTextId = Date.now() + 15;
  const emailTextLine2Id = Date.now() + 102;
  
  // Form elements
  const formWrapperId = Date.now() + 16;
  const formId = Date.now() + 17;
  
  // Name field
  const nameGroupId = Date.now() + 18;
  const nameLabelId = Date.now() + 19;
  const nameInputId = Date.now() + 20;
  
  // Email field
  const emailGroupId = Date.now() + 21;
  const emailLabelId = Date.now() + 22;
  const emailInputId = Date.now() + 23;
  
  // Message field
  const messageGroupId = Date.now() + 24;
  const messageLabelId = Date.now() + 25;
  const messageTextareaId = Date.now() + 26;
  
  // Submit button and status messages
  const submitButtonId = Date.now() + 27;
  const successMessageId = Date.now() + 28;
  const errorMessageId = Date.now() + 29;
  
  const newElements = [
    // Main Contact Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Solar Contact Section',
      parentId: null,
      className: contactSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'contact-section',
        'data-component': 'section',
        'data-section-type': 'contact',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: contactContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'contact-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contactHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'contact-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Contact Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: contactTitleClass,
      styles: {},
      children: [],
      text: 'Get In Touch',
      attributes: {
        'data-element': 'contact-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Contact Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: contactSubtitleClass,
      styles: {},
      children: [],
      text: 'Ready to harness the power of solar energy? Contact JNT & Co. for professional solar installation, CCTV systems, electrical solutions, and security services.',
      attributes: {
        'data-element': 'contact-subtitle'
      }
    },
    
    // Content Container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contactContentClass,
      styles: {},
      children: [infoContainerId, formWrapperId],
      text: '',
      attributes: {
        'data-element': 'contact-content'
      }
    },
    
    // Contact Info Container
    {
      id: infoContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Info',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'contact-info'
      }
    },
    
    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoItemClass,
      styles: {},
      children: [addressIconId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },
    
    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoIconClass,
      styles: {},
      children: [],
      text: '📍',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },
    
    // Address Text Container
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Text Container',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      },
      children: [addressTextLine2Id],
      text: '',
      attributes: {
        'data-element': 'contact-info-text-container'
      }
    },
    
    // Address Text Line 1
    {
      id: addressTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Text',
      parentId: addressTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {
        fontWeight: '600',
        color: '#0f2341',
        marginBottom: '4px'
      },
      children: [],
      text: 'P-724 Block-N North Nazimabad West Karachi',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoItemClass,
      styles: {},
      children: [phoneIconId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },
    
    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoIconClass,
      styles: {},
      children: [],
      text: '📞',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },
    
    // Phone Text Container
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Text Container',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      },
      children: [phoneTextLine2Id],
      text: '',
      attributes: {
        'data-element': 'contact-info-text-container'
      }
    },
    
    // Phone Text
    {
      id: phoneTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Text',
      parentId: phoneTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {
        fontWeight: '600',
        color: '#0f2341',
        marginBottom: '4px'
      },
      children: [],
      text: 'Call Us: 0301-2475122',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoItemClass,
      styles: {},
      children: [emailIconId, emailTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },
    
    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Icon',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoIconClass,
      styles: {},
      children: [],
      text: '✉️',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },
    
    // Email Text Container
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Text Container',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      },
      children: [emailTextLine2Id],
      text: '',
      attributes: {
        'data-element': 'contact-info-text-container'
      }
    },
    
    // Email Text
    {
      id: emailTextLine2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Text',
      parentId: emailTextId,
      hierarchyName: getHierarchyName('div'),
      className: contactInfoTextClass,
      styles: {
        fontWeight: '600',
        color: '#0f2341',
        marginBottom: '4px'
      },
      children: [],
      text: 'Email: jasimarman8@gmail.com',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },
    
    // Form Wrapper
    {
      id: formWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Form Wrapper',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormWrapperClass,
      styles: {},
      children: [formId, successMessageId, errorMessageId],
      text: '',
      attributes: {
        'data-element': 'contact-form-wrapper'
      }
    },
    
    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Contact Form',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('form'),
      className: contactFormClass,
      styles: {},
      children: [nameGroupId, emailGroupId, messageGroupId, submitButtonId],
      method: 'post',
      action: '',
      text: '',
      attributes: {
        'data-element': 'contact-form',
        'data-form-type': 'contact'
      }
    },
    
    // Name Field Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },
    
    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Full Name *',
      attributes: {
        'data-element': 'form-label',
        'for': 'solar-contact-name'
      }
    },
    
    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'text',
        'id': 'solar-contact-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    },

    // Email Field Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Email Address *',
      attributes: {
        'data-element': 'form-label',
        'for': 'solar-contact-email'
      }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: contactFormInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-input',
        'type': 'email',
        'id': 'solar-contact-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Message Field Group
    {
      id: messageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Message Field Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormGroupClass,
      styles: {},
      children: [messageLabelId, messageTextareaId],
      text: '',
      attributes: {
        'data-element': 'form-group'
      }
    },

    // Message Label
    {
      id: messageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Message Label',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('label'),
      className: contactFormLabelClass,
      styles: {},
      children: [],
      text: 'Project Details *',
      attributes: {
        'data-element': 'form-label',
        'for': 'solar-contact-message'
      }
    },

    // Message Textarea
    {
      id: messageTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Message Textarea',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: contactFormTextareaClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'form-textarea',
        'id': 'solar-contact-message',
        'name': 'message',
        'placeholder': 'Tell us about your solar installation needs, property size, energy requirements, or any other services you\'re interested in...',
        'required': 'true',
        'rows': '5'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: contactFormButtonClass,
      styles: {},
      children: [],
      text: 'Get Free Quote',
      attributes: {
        'data-element': 'form-button',
        'type': 'submit'
      }
    },

    // Success Message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormSuccessClass,
      styles: {},
      children: [],
      text: 'Thank you for your interest! We\'ll contact you within 24 hours with a free quote for your solar installation project.',
      attributes: {
        'data-element': 'form-success-message'
      }
    },

    // Error Message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formWrapperId,
      hierarchyName: getHierarchyName('div'),
      className: contactFormErrorClass,
      styles: {},
      children: [],
      text: 'There was an error sending your message. Please try again or call us directly at +92 300 1234567.',
      attributes: {
        'data-element': 'form-error-message'
      }
    }
  ];
  
  return {
    contactSection: newElements[0],
    elements: newElements
  };
}; 